/**
 * AuthImage — authenticated image loader for protected backend endpoints.
 *
 * A plain <img src> can only send cookies, never the Bearer-token fallback
 * (see lib/api.js) — so on iOS Safari / PWA / webviews where cookies are
 * unreliable, protected previews 401'd into a broken-image icon (real bug
 * June 2026: schedule-post preview). This fetches with BOTH cookie +
 * Bearer auth, renders via a blob URL, and shows friendly loading / error
 * states with a retry — a failed image can never look broken again.
 *
 * `silent` — render nothing on loading/error (for decorative backgrounds).
 */
import { useEffect, useState } from "react";
import { getAuthToken } from "../lib/api";

export const AuthImage = ({ src, alt = "", className = "", testId, silent = false, imgProps = {}, keepPrevious = false, onReady }) => {
  const [state, setState] = useState("loading");
  const [objUrl, setObjUrl] = useState(null);
  const [errDetail, setErrDetail] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!src) return undefined;
    let cancelled = false;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 45000);
    // keepPrevious: keep showing the last image while the new one loads
    setState((s) => (keepPrevious && s === "ready" ? "refreshing" : "loading"));
    (async () => {
      try {
        const token = getAuthToken();
        const res = await fetch(src, {
          credentials: "include",
          cache: "no-store",
          signal: ctrl.signal,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) {
          if (!cancelled) {
            setErrDetail(`HTTP ${res.status}`);
            setState("error");
          }
          return;
        }
        const blob = await res.blob();
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        setObjUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
        setState("ready");
        if (onReady) onReady();
      } catch (e) {
        if (!cancelled) {
          setErrDetail(e?.name === "AbortError" ? "timed out" : "network error");
          setState("error");
        }
      } finally {
        clearTimeout(timer);
      }
    })();
    return () => {
      cancelled = true;
      ctrl.abort();
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, attempt]);

  useEffect(() => () => {
    setObjUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  if (!src) return null;

  if ((state === "ready" || state === "refreshing") && objUrl) {
    return (
      <img src={objUrl} alt={alt} className={className} data-testid={testId} {...imgProps} />
    );
  }
  if (silent) return null;
  if (state === "loading") {
    return (
      <div
        className={`${className} flex items-center justify-center animate-pulse bg-muted`}
        data-testid={testId ? `${testId}-loading` : undefined}
      >
        <span className="text-xs text-muted-foreground">Loading preview…</span>
      </div>
    );
  }
  return (
    <div
      className={`${className} flex flex-col items-center justify-center gap-3 p-6 text-center bg-muted`}
      data-testid={testId ? `${testId}-error` : undefined}
    >
      <p className="text-sm text-muted-foreground">
        Couldn&apos;t load the preview image{errDetail ? ` (${errDetail})` : ""}.
      </p>
      <button
        onClick={() => setAttempt((a) => a + 1)}
        data-testid={testId ? `${testId}-retry` : undefined}
        className="text-sm px-4 py-2 rounded-md border font-medium bg-background hover:bg-muted"
      >
        Try again
      </button>
    </div>
  );
};
